<template>
    <UIGridContainer class="category-page">
        <FrontChat />
        <UIGridRow>
            <UIGridCol cols="12" md="10" offset-md="1">
                <h1>{{ category.translations[0].translation.title }}</h1>
                <div class="intro mb-2" v-html="translateGlobalParams(category.translations[0].translation.text)"></div>
                <div class="sub-categories">
                    <router-link
                        v-for="subCategory in category.children"
                        :key="subCategory.id"
                        class="sub-category d-flex justify-content-between align-items-center mb-3"
                        :to="
                            $locale.path({
                                name: 'support-category-subcategory',
                                params: {
                                    category: $route.params.category,
                                    subcategory: subCategory.translations[0].slug,
                                },
                            })
                        "
                    >
                        <div>
                            <p class="title m-0" :class="{ 'my-1': !subCategory._count }">
                                {{ subCategory.translations[0].translation.title }}
                            </p>
                            <p v-if="subCategory._count" class="counter m-0 text-small text-normal">
                                {{
                                    translate('support.questionCount', { value: subCategory._count.categoryQuestions })
                                }}
                            </p>
                        </div>
                        <UIIcon name="chevron-right" class="icon" :hydrate="true"></UIIcon>
                    </router-link>
                </div>

                <SupportFAQCategories
                    :show-title="true"
                    :categories="categories"
                    :current-category-id="`${category.id}`"
                ></SupportFAQCategories>
            </UIGridCol>
        </UIGridRow>
    </UIGridContainer>
</template>

<script>
import { UIGridCol, UIGridRow, UIGridContainer, UIIcon } from '@dundle/ui/components';
import { SupportFAQCategories, FrontChat } from '~/components/support';
import TranslationMixin from '~/mixins/TranslationMixin';
import BreadcrumbMixin from '~/mixins/BreadcrumbMixin';

export default {
    components: {
        FrontChat,
        UIGridCol,
        UIGridRow,
        UIGridContainer,
        SupportFAQCategories,
        UIIcon,
    },

    mixins: [TranslationMixin, BreadcrumbMixin],

    async asyncData({ app, route, error }) {
        try {
            const [category, categories] = await Promise.all([
                app.$api.faq.getCategoryBySlug(route.params.category),
                app.$api.faq.getCategories(),
            ]);

            return {
                category,
                categories,
            };
        } catch {
            return error({ message: 'Page not found', statusCode: 404 });
        }
    },
    head() {
        return {
            title: this.translateParams(this.category.translations?.[0].translation?.details.title),
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content: this.category.translations?.[0].translation?.details.description,
                },
                {
                    hid: 'robots',
                    name: 'robots',
                    content: this.$locale.isTarget() && this.isCurrentLanguage ? 'index, follow' : 'noindex, nofollow',
                },
            ],
        };
    },

    computed: {
        isCurrentLanguage() {
            return this.category.translations?.[0].translation?.locale.code === this.$locale.language;
        },
    },

    breadcrumb({ app }, data) {
        return [
            {
                name: app.$locale.regio.toLowerCase(),
                link: app.$locale.path('index'),
                url: 'https://' + app.$locale.domain + app.$locale.path('index'),
            },
            {
                name: 'Support',
                link: app.$locale.path('support'),
                url: 'https://' + app.$locale.domain + app.$locale.path('support'),
            },
            {
                name: data.category.translations[0].translation.title,
                link: app.$locale.path({
                    name: 'support-category',
                    params: { category: data.category.translations?.[0].slug },
                }),
                url:
                    'https://' +
                    app.$locale.domain +
                    app.$locale.path({
                        name: 'support-category',
                        params: { category: data.category.translations?.[0].slug },
                    }),
            },
        ];
    },
    mounted() {
        this.$gtm?.push({ event: `page_view_support_category`, value: this.category.name });
    },
};
</script>

<style lang="scss" scoped>
.category-page {
    ::v-deep h2 {
        margin-top: 4rem;
        margin-bottom: 1rem;
    }
}
.sub-category {
    border: 1px solid $color-grey-3;
    border-radius: $border-radius-default;
    padding: 0.5rem 1rem;

    @media screen and (min-width: 768px) {
        &:hover {
            text-decoration: none;

            .title,
            .icon {
                color: $color-primary;
            }
        }
    }

    .icon {
        color: $color-text-bold;
    }

    .title {
        color: $color-text-bold;
        font-weight: 700;
    }
    .counter {
        color: $color-primary;
        font-style: italic;
    }
}
</style>
<i18n src="@dundle/locale/data/support.json"></i18n>
